import isEqual from 'lodash/isEqual'
import React from 'react'

import { storageAvailable } from '@Globals/Storage'

let storageCache: Storage

function getStorage() {
  if (storageCache) {
    return storageCache
  }

  if (storageAvailable('localStorage')) {
    storageCache = localStorage
  } else if (storageAvailable('sessionStorage')) {
    storageCache = sessionStorage
  } else {
    const storage = new Map<string, string>()
    storageCache = {
      getItem: (k: string) => storage.get(k) ?? null,
      setItem: (k: string, v: string) => { storage.set(k, v) },
      removeItem: (k: string) => { storage.delete(k) },
    } as Storage
  }

  return storageCache
}

const stores = new Map<string, ReturnType<typeof getStore>>()

type GetStoreType<T> = {
  getInitialValue: () => T
  getSnapshot: () => T
  setValue: (value: T) => void
  subscribe: (listener: () => void) => () => void
}

export function getStore<T>(key: string, initialValue: T): GetStoreType<T> {
  if (stores.has(key)) {
    return stores.get(key) as GetStoreType<T>
  }

  let currentValue: T
  const storage = getStorage()
  const listeners = new Set<() => void>()

  const readValue = () => {
    const data = storage.getItem(key)

    let value: T = initialValue
    if (data != null) {
      try {
        value = JSON.parse(data) as T
      } catch {
        value = initialValue
      }
    }

    if (isEqual(value, currentValue)) {
      return
    }

    currentValue = value
    listeners.forEach(l => l())
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
      if (e.key === key) {
        readValue()
      }
    })
  }

  readValue()

  const store: GetStoreType<T> = {
    getInitialValue() {
      return initialValue
    },
    getSnapshot() {
      return currentValue
    },
    setValue(value: T) {
      if (isEqual(value, currentValue)) {
        return
      }

      currentValue = value
      storage.setItem(key, JSON.stringify(value))
      listeners.forEach(l => l())
    },
    subscribe(listener: () => void) {
      listeners.add(listener)

      return () => {
        listeners.delete(listener)
      }
    },
  }

  stores.set(key, store)

  return store
}

export function useStore<T>(store: GetStoreType<T>) {
  return React.useSyncExternalStore(store.subscribe, store.getSnapshot, store.getInitialValue)
}
